import { FvButton } from '@fv/client-components'
import { addressFuncs, useFormModelState } from '@fv/client-core'
import type { ClientAddressLookupResult } from '@fv/client-types'
import { customsBrokerTypes } from '@fv/models'

import { ValidatedForm } from '@/components/inputs'
import { AddressInput } from '@/components/inputs/AddressInput'
import { ContactPhoneInput } from '@/components/inputs/ContactPhoneInput'
import { InputGroup, InputGroupWrapper } from '@/components/inputs/InputGroup'

import { HasPermission } from '../auth'
import { addressMapper } from './addressFuncs'
import type { FormAddress } from './types'
import { useAddressBookConfig, useCloseAddressBookEdit } from './useAddressBook'
import { useCreateAddress } from './useCreateAddress'
import { useUpdateAddress } from './useUpdateAddress'

type Props = {
  initialValue?: ClientAddressLookupResult
}
export const CustomsBrokerAddressForm = ({ initialValue }: Props) => {
  const closeAddressBookEdit = useCloseAddressBookEdit()
  const config = useAddressBookConfig()

  const createAddress = useCreateAddress(config)
  const updateAddress = useUpdateAddress(config)
  const mutation = initialValue?._id ? updateAddress : createAddress

  const { register, value, setValue } = useFormModelState<FormAddress>({
    initialValue: addressMapper.buildFormAddress(initialValue),
  })
  const handleSubmit = async () => {
    await mutation.mutateAsync({
      ...value,
      id: initialValue?._id,
      type: config.type,
    })
    closeAddressBookEdit()
  }
  return (
    <ValidatedForm
      onValidSubmit={handleSubmit}
      className="flex h-full flex-col justify-between"
    >
      <InputGroup
        required
        inputType="text"
        inputProps={register('company')}
        label="Company Name"
      />
      <div className="grid grid-cols-3 gap-x-2">
        <InputGroup
          inputType="text"
          inputProps={register('address')}
          label="Address"
        />
        <InputGroup
          inputType="text"
          inputProps={register('address2')}
          label="Address 2"
        />
        <InputGroupWrapper name="cityStateZip" label="City, State, Zip">
          <AddressInput
            context={'customs-broker'}
            formatAddress={addressFuncs.cityStateZip}
            isClearable={false}
            location={value}
            name="cityStateZip"
            onLocationChange={x => {
              setValue(p => ({
                ...p,
                city: x?.city || '',
                state: x?.state || '',
                postalCode: x?.postalCode || '',
                country: x?.country || 'us',
                adminArea3: x?.adminArea3,
              }))
            }}
            placeholder="Zip/postal code"
          />
        </InputGroupWrapper>
      </div>
      <div className="grid grid-cols-3 gap-x-2">
        <InputGroup
          inputType="select"
          inputProps={{
            ...register('type'),
            options: ['', ...customsBrokerTypes],
          }}
          label="Type"
        />
        <InputGroup
          inputType="text"
          inputProps={register('contactEmail')}
          label="Email"
        />
        <ContactPhoneInput
          name="contactPhone"
          required
          onChange={x =>
            setValue(p => ({ ...p, contactPhone: x.contactPhone }))
          }
          values={{
            contactPhone: value.contactPhone || '',
          }}
        />
      </div>

      <HasPermission name="manage-addresses">
        <div className="flyout-footer-actions flex items-center">
          <FvButton
            icon="check"
            theme="primary"
            type="submit"
            loading={mutation.isPending}
          >
            {initialValue?._id ? 'Update' : 'Add'} customs broker address
          </FvButton>
          <FvButton
            loading={mutation.isPending}
            icon="times"
            theme="plain"
            type="button"
            onClick={closeAddressBookEdit}
          >
            Cancel
          </FvButton>
        </div>
      </HasPermission>
    </ValidatedForm>
  )
}
